import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
import { newPasswordPathFor } from '../contexts/JWTContext';
// routes
import { PATH_CLIENT, PATH_DASHBOARD, PATH_LINGUIST } from '../routes/paths';
// the sky a guard waits on
import { AUTH_PORTALS } from '../layouts/auth/portals';
import SessionPending from './SessionPending';

// ----------------------------------------------------------------------
// The guest guards wrap the sign-in screens: an authenticated visitor is sent
// on to their dashboard. The one exception is a session that came from a
// recovery or invite link — it exists to set a password and must reach the
// new-password page, which sits OUTSIDE these guards for exactly that reason.
//
// They WAIT for the session to be resolved (2026-10-11). Rendering the sign-in
// whenever `isAuthenticated` was false meant rendering it while it was merely
// unknown: back from Microsoft, the form the person had just got past was on
// screen for the second it took supabase-js to take the tokens and
// /verify-token to answer, and a signed-in reload of a sign-in page did the
// same in miniature. Until `isInitialized` the guard shows `SessionPending`,
// and the page it guards is not mounted at all.
// ----------------------------------------------------------------------

UserGuestGuard.propTypes = {
  children: PropTypes.node,
  userType: PropTypes.string
};

export default function UserGuestGuard({ children, userType }) {
  const { isAuthenticated, isInitialized, type, passwordRecovery } = useAuth();

  if (!isInitialized) {
    return <SessionPending portal={AUTH_PORTALS.intranet} />;
  }
  if (isAuthenticated && passwordRecovery) {
    return <Navigate to={newPasswordPathFor(type)} replace />;
  }
  if (isAuthenticated && type === userType) {
    return <Navigate to={PATH_DASHBOARD.root} />;
  }
  return <>{children}</>;
}

LinguistGuestGuard.propTypes = {
  children: PropTypes.node,
  userType: PropTypes.string
};

export function LinguistGuestGuard({ children }) {
  const { isAuthenticated, isInitialized, user, passwordRecovery } = useAuth();

  if (!isInitialized) {
    return <SessionPending portal={AUTH_PORTALS.vendor} />;
  }
  if (isAuthenticated && passwordRecovery) {
    return <Navigate to={newPasswordPathFor(user?.type)} replace />;
  }
  if (isAuthenticated && user?.type === 'Linguist') {
    if (user?.profileStatus === 'unverified') {
      return <Navigate to={PATH_LINGUIST.user.verifyEmail} />;
    }
    if (!user?.isOnBoardingCompleted) {
      return <Navigate to={PATH_LINGUIST.user.onBoarding} />;
    }
    return <Navigate to={PATH_LINGUIST.user.dashboard} />;
  }
  if (isAuthenticated && user?.type === 'Technician') {
    return <Navigate to={PATH_LINGUIST.technician.technicianSheetsList} />;
  }
  if (isAuthenticated && user?.type === 'Formatter') {
    return <Navigate to={PATH_LINGUIST.formatter.formattingFilesList} />;
  }
  if (isAuthenticated && user?.type === 'Marketeer') {
    return <Navigate to={PATH_LINGUIST.marketeer.marketingChannelList} />;
  }
  return <>{children}</>;
}

ClientGuestGuard.propTypes = {
  children: PropTypes.node,
  userType: PropTypes.string
};

export function ClientGuestGuard({ children }) {
  const { isAuthenticated, isInitialized, user, passwordRecovery } = useAuth();

  if (!isInitialized) {
    return <SessionPending portal={AUTH_PORTALS.client} />;
  }
  if (isAuthenticated && passwordRecovery) {
    return <Navigate to={newPasswordPathFor(user?.type)} replace />;
  }
  if (isAuthenticated && user?.type === 'Client') {
    return <Navigate to={PATH_CLIENT.client.dashboard} />;
  }
  return <>{children}</>;
}
